import React from "react";
import { flattenSpaces, getSpaceCapabilities, getSpaceVisualLayout } from "../spatialModel";

function displayType(value) {
  return value ? value.replaceAll("_", " ") : "Space";
}

function CapabilityBadge({ capabilities }) {
  if (capabilities.length === 0) return null;
  return (
    <span className="digital-twin-space-cap-badge" aria-hidden="true">
      <svg className="digital-twin-cap-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M9.6 0.8 2.8 9.1h4.1l-0.7 6.1 7-8.5H9.1z" /></svg>
      <span className="digital-twin-cap-count">{capabilities.length}</span>
      <span className="digital-twin-cap-tooltip" role="tooltip">
        {capabilities.map((cap) => (
          <span key={cap.id} className="digital-twin-cap-tooltip-row">
            <span className="digital-twin-cap-tooltip-dot" />
            {cap.label}
          </span>
        ))}
      </span>
    </span>
  );
}

export default function DigitalTwinCanvas({ spaces, capabilities, selectedSpaceId, onSelect, showHeading = true }) {
  const layout = getSpaceVisualLayout(spaces);
  const placedIds = new Set(layout.map((space) => space.id));
  const parentIds = new Set(layout.map((space) => space.parentId).filter(Boolean));
  const unplaced = flattenSpaces(spaces).filter((space) => !placedIds.has(space.id));

  return (
    <section
      className="digital-twin-canvas-section"
      aria-labelledby={showHeading ? "twin-canvas-title" : undefined}
      aria-label={showHeading ? undefined : "Studio spatial schematic"}
    >
      <div className="digital-twin-section-heading">
        <div>
          {showHeading && <h2 id="twin-canvas-title">Spatial Representation</h2>}
        </div>
        <span className="digital-twin-scale-note">Schematic, not to scale</span>
      </div>
      <p className="digital-twin-section-copy">
        Explore the Studio as a living digital representation of its spaces, capabilities and operational context.
      </p>

      <div className="digital-twin-canvas" role="group" aria-label="Studio spatial representation">
        <div className="digital-twin-canvas-grid" aria-hidden="true" />
        {layout.map((space) => {
          const { bounds } = space;
          const spaceCapabilities = getSpaceCapabilities(space, capabilities);
          const selected = selectedSpaceId === space.id;
          return (
            <button
              type="button"
              key={space.id}
              className={`digital-twin-space digital-twin-space-depth-${Math.min(space.depth, 3)}${parentIds.has(space.id) ? " has-children" : ""}${selected ? " is-selected" : ""}`}
              style={{
                left: `${bounds.x}%`,
                top: `${bounds.y}%`,
                width: `${bounds.width}%`,
                height: `${bounds.height}%`,
                zIndex: space.depth + 1,
              }}
              aria-label={`${space.label}, ${displayType(space.type)}${spaceCapabilities.length ? `, ${spaceCapabilities.length} ${spaceCapabilities.length === 1 ? "capability" : "capabilities"}: ${spaceCapabilities.map((c) => c.label).join(", ")}` : ""}`}
              aria-pressed={selected}
              onClick={() => onSelect(space.id)}
            >
              <span className="digital-twin-space-body">
                <span className="digital-twin-space-label">{space.label}</span>
                <span className="digital-twin-space-type">{displayType(space.type)}</span>
                <CapabilityBadge capabilities={spaceCapabilities} />
              </span>
            </button>
          );
        })}
        {layout.length === 0 && (
          <div className="digital-twin-canvas-empty">No visual layout is configured.</div>
        )}
      </div>

      {unplaced.length > 0 && (
        <div className="digital-twin-unplaced" aria-label="Spaces without schematic geometry">
          <span>Not placed</span>
          {unplaced.map((space) => (
            <button type="button" key={space.id} onClick={() => onSelect(space.id)}>
              {space.label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}